<template>
  <v-app>
  <header
    class="guided-recording-public__top-bar first-top-bar px-xs-2 px-sm-5 px-md-10"
    data-cy="guidedRecording-public-topBar"
  >
    <div class="main-logo mx-1">
      <img
        v-if="pageTheme === 'dark'"
        src="@/assets/images/tw-main-logo-dark.png"
        alt="TubeWay Logo"
      >
      <img
        v-else
        src="@/assets/images/tw-main-logo-light.png"
        alt="TubeWay Logo"
      >
    </div>
    <div class="flex-grow-1" />
    <div v-if="getExpireAt" class="mr-4" data-cy="guidedRecording-public-expiry">
      {{ $t('routes.accesslink.linkExpiresAt') }} {{ isoToDate(getExpireAt) }}
      ({{ $t('common.time.days', getExpiryDays) }})
    </div>
  </header>

  <main class="guided-recording-public__main">
    <v-container class="guided-recording-public" :class="isRecordingPage ? 'pa-3' : 'pa-4 pa-sm-6'" data-cy="guidedRecording-public">
      <v-row justify="center">
        <v-col cols="12" class="guided-recording-public__column">
          <header class="guided-recording-public__header" :class="isRecordingPage ? 'mb-2' : 'mb-6'">
            <h1 class="guided-recording-public__heading mb-0" :class="{ 'guided-recording-public__heading--initial': !isRecordingPage }">
              {{ $t('routes.guidedRecording.publicHeading') }}
            </h1>
          </header>

        <div v-if="isLoading" class="text-center py-12" role="status" aria-live="polite" data-cy="guidedRecording-public-loading">
          <v-progress-circular indeterminate color="primary" class="mb-3" aria-hidden="true" />
          <p>{{ $t('routes.guidedRecording.loadingInvitation') }}</p>
        </div>

        <v-alert
          v-else-if="isUnavailable"
          type="error"
          variant="tonal"
          role="alert"
          data-cy="guidedRecording-public-unavailable"
        >
          <template #title>
            <h2
              ref="unavailableHeading"
              class="guided-recording-public__unavailable-heading text-body-1 font-weight-medium mb-0"
              tabindex="-1"
            >
              {{ $t('routes.guidedRecording.unavailable') }}
            </h2>
          </template>
        </v-alert>

        <section
          v-else-if="invitationLoadFailed"
          aria-labelledby="guided-recording-public-invitation-retry"
          data-cy="guidedRecording-public-invitationRetry"
        >
          <v-alert
            v-if="sessionError"
            type="error"
            variant="tonal"
            role="alert"
            class="mb-4"
            data-cy="guidedRecording-public-sessionError"
          >
            {{ sessionError }}
          </v-alert>
          <h2 id="guided-recording-public-invitation-retry" class="text-h5 mb-2">
            {{ $t('routes.guidedRecording.sessionUnavailableHeading') }}
          </h2>
          <v-btn
            color="primary"
            :loading="isLoading"
            @click="loadInvitation"
          >
            {{ $t('routes.guidedRecording.retrySession') }}
          </v-btn>
        </section>

        <v-row v-else-if="invitation" align="start" class="guided-recording-public__content" :class="{ 'guided-recording-public__content--recording': isRecordingPage }">
          <v-col cols="12" class="guided-recording-public__sidebar">
            <v-card color="surface" :class="isRecordingPage ? 'pa-3 mb-3' : 'pa-4 pa-sm-6 mb-4'" data-cy="guidedRecording-public-invitation">
              <h2 class="text-h5 mb-2">{{ displayedConfiguration.title }}</h2>
              <p v-if="displayedConfiguration.introduction" class="guided-recording-public__copy text-body-1 mb-4">
                {{ displayedConfiguration.introduction }}
              </p>
              <p v-if="!isRecordingPage && displayedConfiguration.instructions" class="guided-recording-public__copy text-body-2 mb-0">
                {{ displayedConfiguration.instructions }}
              </p>
              <details v-if="isRecordingPage" class="guided-recording-public__instructions" open>
                <summary class="text-subtitle-2">{{ $t('routes.guidedRecording.instructionsHeading') }}</summary>
                <p v-if="displayedConfiguration.instructions" class="guided-recording-public__copy text-body-2">
                  {{ displayedConfiguration.instructions }}
                </p>
                <ul class="text-body-2">
                  <li>{{ $t('routes.guidedRecording.speakClearly') }}</li>
                  <li>{{ $t('routes.guidedRecording.alwaysFaceCamera') }}</li>
                  <li>{{ $t('routes.guidedRecording.orientationGuidance') }}</li>
                </ul>
              </details>
            </v-card>

            <v-card
              v-if="isRecordingPage"
              color="surface"
              class="guided-recording-public__progress-rail pa-4"
              data-cy="guidedRecording-public-progress"
            >
              <section :aria-labelledby="progressHeadingId">
                <h2 :id="progressHeadingId" class="text-h6 mb-1">
                  {{ $t('routes.guidedRecording.questionProgressHeading') }}
                </h2>
                <p class="text-body-2 mb-3">
                  {{ $t('routes.guidedRecording.questionProgressSummary', {
                    completed: resolvedQuestionCount,
                    total: sessionQuestions.length,
                  }) }}
                </p>
                <v-progress-linear
                  :model-value="progressPercentage"
                  color="primary"
                  height="8"
                  rounded
                  role="progressbar"
                  :aria-label="$t('routes.guidedRecording.questionProgressSummary', {
                    completed: resolvedQuestionCount,
                    total: sessionQuestions.length,
                  })"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-valuenow="progressPercentage"
                  class="mb-4"
                />
                <ol class="guided-recording-public__progress-list">
                  <li
                    v-for="question in sessionQuestions"
                    :key="question.order"
                    class="guided-recording-public__progress-item"
                    :class="{
                      'guided-recording-public__progress-item--current': question.order === currentQuestion?.order,
                      'guided-recording-public__progress-item--completed': question.state === 'completed',
                      'guided-recording-public__progress-item--skipped': question.state === 'skipped',
                    }"
                    :aria-current="question.order === currentQuestion?.order ? 'step' : undefined"
                  >
                    <button
                      type="button"
                      class="guided-recording-public__question-button"
                      :disabled="!isSessionActive || isSessionActionDisabled"
                      :aria-current="question.order === currentQuestion?.order ? 'step' : undefined"
                      :data-cy="`guidedRecording-public-selectQuestion-${question.order}`"
                      @click="selectQuestion(question.order)"
                    >
                    <v-icon
                      :color="questionUploadStateColor(question) || questionStateColor(question.state)"
                      aria-hidden="true"
                    >
                      {{ questionUploadStateIcon(question) || questionStateIcon(question.state) }}
                    </v-icon>
                    <div>
            <span class="d-block text-body-2 font-weight-medium">
              {{ $t('routes.guidedRecording.questionNumber', { number: question.order }) }}
            </span>
            {{ ' ' }}
            <span class="d-block text-caption">
              {{ questionRequirementLabel(question) }} · {{ questionStateLabel(question.state) }}
            </span>
            <span
              v-if="recoveryUploads.length"
              class="d-block text-caption"
              :data-cy="`guidedRecording-public-uploadState-${question.order}`"
            >
              {{ questionUploadStateLabel(question) }}
            </span>
                    </div>
                    </button>
                  </li>
                </ol>
              </section>
            </v-card>
            <v-card v-if="isRecordingPage" color="surface" class="pa-4 mt-3" data-cy="guidedRecording-public-deviceSettings">
              <h2 class="text-subtitle-1 mb-2">{{ $t('routes.guidedRecording.deviceSettings') }}</h2>
              <v-select
                v-model="selectedVideoDeviceId"
                :items="videoDevices"
                item-title="label"
                item-value="deviceId"
                :label="$t('routes.guidedRecording.camera')"
                :disabled="devicesDisabled"
                density="compact"
                data-cy="guidedRecording-recorder-camera"
                @update:model-value="changeDevicePreference"
              />
              <v-select
                v-model="selectedAudioDeviceId"
                :items="audioDevices"
                item-title="label"
                item-value="deviceId"
                :label="$t('routes.guidedRecording.microphone')"
                :disabled="devicesDisabled"
                density="compact"
                data-cy="guidedRecording-recorder-microphone"
                @update:model-value="changeDevicePreference"
              />
              <v-switch
                v-model="noiseSuppression"
                color="primary"
                :label="$t('routes.guidedRecording.noiseSuppression')"
                :disabled="devicesDisabled || !noiseSuppressionSupported"
                hide-details
                data-cy="guidedRecording-public-noiseSuppression"
                @update:model-value="changeDevicePreference"
              />
              <p v-if="!noiseSuppressionSupported" class="text-caption">
                {{ $t('routes.guidedRecording.noiseSuppressionUnsupported') }}
              </p>
              <p v-else-if="sourceStream && noiseSuppressionApplied !== noiseSuppression" class="text-caption">
                {{ $t('routes.guidedRecording.noiseSuppressionNotApplied') }}
              </p>
              <v-switch
                v-model="blurEnabled"
                color="primary"
                :label="$t('routes.guidedRecording.backgroundBlur')"
                :disabled="devicesDisabled || !blurSupported"
                hide-details
                data-cy="guidedRecording-public-backgroundBlur"
              />
              <p v-if="blurState === 'warming'" class="text-caption" role="status">
                {{ $t('routes.guidedRecording.blurLoading') }}
              </p>
              <p v-if="!blurSupported" class="text-caption" role="status" data-cy="guidedRecording-public-blurFailure">
                {{ $t(`routes.guidedRecording.${blurReason || 'blurUnavailable'}`) }}
              </p>
            </v-card>
          </v-col>

          <v-col ref="workspace" cols="12" class="guided-recording-public__workspace">
            <v-card color="surface" :class="isRecordingPage ? 'pa-3' : 'pa-4 pa-sm-6'" data-cy="guidedRecording-public-session">
              <v-alert
                v-if="sessionError"
                type="error"
                variant="tonal"
                role="alert"
                class="mb-4"
                data-cy="guidedRecording-public-sessionError"
              >
                {{ sessionError }}
              </v-alert>
              <p
                v-if="isRecordingPage && sessionStatus"
                class="guided-recording-public__session-status text-body-2 mb-4"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-cy="guidedRecording-public-sessionStatus"
              >
                {{ sessionStatus }}
              </p>

              <v-alert
                v-if="recoveryBannerText"
                type="info"
                variant="tonal"
                role="status"
                aria-live="polite"
                class="mb-4"
                data-cy="guidedRecording-public-recoveryBanner"
              >
                {{ recoveryBannerText }}
              </v-alert>

              <template v-if="!recordingPageEntered && recoveryState !== 'failed'">
                <h2 class="text-h6 mb-2">{{ $t('routes.guidedRecording.startSession') }}</h2>
                <p class="text-body-2 mb-4">{{ $t('routes.guidedRecording.sessionStartDescription') }}</p>
                <v-form @submit.prevent="startSession">
                  <v-text-field
                    ref="participantName"
                    v-model="participantDisplayName"
                    :label="$t('routes.guidedRecording.participantName')"
                    :error-messages="participantNameError"
                    :disabled="isRecoveryInProgress"
                    :readonly="Boolean(recoveryAttestation)"
                    maxlength="30"
                    counter="30"
                    required
                    data-cy="guidedRecording-public-participantName"
                  />
                  <section
                    v-if="consentRequired"
                    class="guided-recording-public__consent mb-4"
                    data-cy="guidedRecording-public-consent"
                  >
                    <MdPreview
                      :modelValue="invitation.consent.statement"
                      :theme="pageTheme"
                      language="en-US"
                      class="guided-recording-public__consent-statement mb-3"
                      data-cy="guidedRecording-public-consentStatement"
                    />
                    <router-link to="/account/policy" target="_blank" rel="noopener">
                      {{ $t('routes.guidedRecording.consentPrivacyPolicy') }}
                    </router-link>
                    <v-checkbox
                      ref="consent"
                      v-model="consentAccepted"
                      :label="$t('routes.guidedRecording.consent')"
                      :disabled="isRecoveryInProgress"
                      :error="Boolean(consentError)"
                      :error-messages="consentError"
                      required
                    />
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      {{ $t('routes.guidedRecording.consentPolicyVersion', {
                        version: invitation.consent.statementVersion,
                      }) }}
                    </p>
                  </section>
                  <v-btn
                    type="submit"
                    color="primary"
                    :loading="isStartingSession || isRecoveryInProgress"
                    :disabled="isRecoveryInProgress"
                    data-cy="guidedRecording-public-startSession"
                  >
                    <v-icon start aria-hidden="true">mdi-arrow-right-circle-outline</v-icon>
                    {{ $t('routes.guidedRecording.continueToRecorder') }}
                  </v-btn>
                </v-form>
              </template>

              <section
                v-else-if="(!sessionProjection || !recordingPageEntered) && !isRecoveryInProgress"
                aria-labelledby="guided-recording-session-retry"
                data-cy="guidedRecording-public-sessionRetry"
              >
                <h2 id="guided-recording-session-retry" class="text-h6 mb-2">
                  {{ $t('routes.guidedRecording.sessionUnavailableHeading') }}
                </h2>
                <p class="text-body-2 mb-4">{{ $t('routes.guidedRecording.sessionReadError') }}</p>
                <v-btn
                  color="primary"
                  :loading="isSessionActionInProgress || isLoading"
                  @click="retrySession"
                >
                  {{ $t('routes.guidedRecording.retrySession') }}
                </v-btn>
              </section>

              <section
                v-else-if="recordingPageEntered && isSubmitted"
                aria-labelledby="guided-recording-submission-complete"
                data-cy="guidedRecording-public-submitted"
              >
                <v-alert type="success" variant="tonal" role="status" aria-live="polite">
                  <h2 id="guided-recording-submission-complete" class="text-h6 mb-2">
                    {{ $t('routes.guidedRecording.submissionCompleteHeading') }}
                  </h2>
                  <p class="mb-0">{{ $t('routes.guidedRecording.submissionComplete') }}</p>
                </v-alert>
              </section>

              <template v-else-if="isRecordingPage">
                <section
                  class="guided-recording-public__current-question"
                  :aria-labelledby="currentQuestionHeadingId"
                  data-cy="guidedRecording-public-currentQuestion"
                >
                  <div class="guided-recording-public__question-header d-flex align-center justify-space-between ga-2 mb-2">
                    <div>
                      <h2
                        :id="currentQuestionHeadingId"
                        ref="currentQuestionHeading"
                        class="guided-recording-public__current-heading text-h5"
                        tabindex="-1"
                      >
                        {{ $t('routes.guidedRecording.questionProgress', {
                          current: currentQuestion.order,
                          total: sessionQuestions.length,
                        }) }}
                      </h2>
                    </div>
                <nav
                  class="guided-recording-public__question-actions"
                  :aria-label="$t('routes.guidedRecording.questionNavigation')"
                >
                  <v-btn
                    variant="outlined"
                    size="small"
                    :disabled="!canGoPrevious"
                    :loading="isSessionActionInProgress && sessionAction === 'previous'"
                    data-cy="guidedRecording-public-previousQuestion"
                    @click="previousQuestion"
                  >
                    <v-icon start aria-hidden="true">mdi-arrow-left</v-icon>
                    {{ $t('routes.guidedRecording.previousQuestion') }}
                  </v-btn>
                  <v-btn
                    v-if="canSkip"
                    color="secondary"
                    variant="outlined"
                    size="small"
                    :disabled="isSessionActionDisabled"
                    :loading="isSessionActionInProgress && sessionAction === 'skip'"
                    data-cy="guidedRecording-public-skipQuestion"
                    @click="skipQuestion"
                  >
                    <v-icon start aria-hidden="true">mdi-skip-next-outline</v-icon>
                    {{ $t('routes.guidedRecording.skipQuestion') }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    size="small"
                    :disabled="!canGoNext"
                    :loading="isSessionActionInProgress && sessionAction === 'next'"
                    data-cy="guidedRecording-public-nextQuestion"
                    @click="nextQuestion"
                  >
                    {{ $t('routes.guidedRecording.nextQuestion') }}
                    <v-icon end aria-hidden="true">mdi-arrow-right</v-icon>
                  </v-btn>
                </nav>
                    <span class="guided-recording-public__question-kind text-body-2">
                      {{ questionRequirementLabel(currentQuestion) }}
                    </span>
                  </div>
                  <p class="guided-recording-public__question-text text-body-1 mb-0">
                    {{ currentQuestion.text }}
                  </p>
                  <details v-if="currentQuestion.note" class="guided-recording-public__question-detail mt-2">
                    <summary class="text-subtitle-2">{{ $t('routes.guidedRecording.questionNote') }}</summary>
                    <p class="guided-recording-public__copy mb-0">{{ currentQuestion.note }}</p>
                  </details>
                </section>
                <v-alert
                  v-if="currentQuestion.state === 'completed'"
                  type="success"
                  variant="tonal"
                  class="mt-3"
                  data-cy="guidedRecording-public-questionCompleted"
                >
                  {{ $t('routes.guidedRecording.questionConfirmed') }}
                </v-alert>

                <GuidedAudioResultPanel
                  v-if="currentQuestionAudioResult"
                  class="mt-4"
                  :active-action="audioAction"
                  :is-acting="isAudioActionInProgress"
                  :is-resolved="currentQuestionAudioResult.resolved"
                  :question-number="currentQuestion.order"
                  :question-total="sessionQuestions.length"
                  :status="currentQuestionAudioResult.status"
                  @record-again="recordAudioAgain"
                  @continue-anyway="overrideAudioWarning"
                  @continue="continueAudioUnavailable"
                  @retry="retryAudioResultProjection"
                />
                <v-btn
                  v-if="currentQuestionAudioResult?.status === 'pass' && currentQuestion.state !== 'completed'"
                  class="mt-2"
                  :loading="isAudioActionInProgress"
                  :disabled="isAudioActionInProgress || isSubmitting"
                  @click="completeArrivedAudioResult"
                >
                  {{ $t('routes.guidedRecording.retryFinalization') }}
                </v-btn>
                <v-alert
                  v-if="isTakeProcessing"
                  type="info"
                  variant="tonal"
                  role="status"
                  aria-live="polite"
                  class="mt-3"
                  data-cy="guidedRecording-public-processingTake"
                >
                  {{ $t('routes.guidedRecording.processingTake') }}
                </v-alert>


                <v-alert
                  v-if="currentQuestionTakeNotDurable"
                  type="warning"
                  variant="tonal"
                  role="alert"
                  class="mb-4"
                  data-cy="guidedRecording-public-takeNotDurable"
                >
                  {{ $t('routes.guidedRecording.recovery.takeNotDurable') }}
                </v-alert>

                <section
                  v-if="!currentQuestionAudioResult && !isTakeProcessing && currentQuestion.state !== 'completed'"
                  class="guided-recording-public__recording mt-3"
                  :aria-label="$t('routes.guidedRecording.recorderSection')"
                >
                  <v-alert v-if="take?.reviewNoticeKey" type="warning" variant="tonal" class="mb-2" role="status">
                    {{ $t(`routes.guidedRecording.${take.reviewNoticeKey}`) }}
                  </v-alert>
                  <GuidedRecorder
                    v-if="!take"
                    :key="questionComponentRecorderKey"
                    :source-stream="sourceStream"
                    :source-requesting="sourceRequesting"
                    :source-error-key="sourceErrorKey"
                    :blur-enabled="blurEnabled"
                    :labelled-by="currentQuestionHeadingId"
                    @busy-change="handleRecorderBusy"
                    @request-preview="enablePreview"
                    @release-source="releaseSource"
                    @capability-change="handleRecorderCapability"
                    :maximum-duration-seconds="currentQuestion.maximumDurationSeconds"
                    @recorded="setTake"
                    @timer-tick="handleTimerTick"
                    @is-recording="handleIsRecording"
                  >
                    <template #below-preview>
                      <GuidedAutocue
                        v-model:alignment="autocueAlignment"
                        v-model:font-size="autocueFontSize"
                        v-model:scroll-speed="autocueScrollSpeed"
                        :script="currentQuestion.script"
                        :elapsed-seconds="recorderElapsedSeconds"
                        :remaining-seconds="recorderRemainingSeconds"
                        :is-recording="recorderIsRecording"
                        @telemetry="handleAutocueTelemetry"
                      />
                    </template>
                  </GuidedRecorder>
                  <GuidedTakeReview
                    v-else
                    :key="questionComponentReviewKey"
                    :maximum-duration-seconds="currentQuestion.maximumDurationSeconds"
                    :take="take"
                    :question-number="currentQuestion.order"
                    :question-total="sessionQuestions.length"
                    :can-discard="!uploadDetails"
                    :is-cancelling="isCancelling"
                    :is-uploading="isSubmitting"
                    :progress="submissionProgress"
                    :can-retry="isRetryableSubmissionFailure"
                    :upload-error="submissionError"
                    @accept="acceptTake"
                    @discard="discardTake"
                    @cancel-upload="cancelUpload"
                    @retry-upload="retryUpload"
                  />
                </section>
              </template>
            </v-card>
          </v-col>
        </v-row>
        </v-col>
      </v-row>
    </v-container>
  </main>
  </v-app>
</template>

<script>
import { MdPreview } from 'md-editor-v3';
import { markRaw } from 'vue';
import { useTheme } from 'vuetify';
import axios from 'axios';
import Api from '@/api/Api.js';
import { isoToDate } from '@/components/formatters.js';
import { DEFAULT_THEME_COLORS } from '@/components/vuetify.js';
import { useMainStore } from '@/stores/MainStore.js';
import {
  isGuidedRecordingFinalizationRetryable,
} from '@/api/modules/GuidedRecordingCalls.js';
import GuidedAudioResultPanel from '@/routes/guided-recording/components/GuidedAudioResultPanel.vue';
import GuidedAutocue from '@/routes/guided-recording/components/GuidedAutocue.vue';
import GuidedRecorder from '@/routes/guided-recording/components/GuidedRecorder.vue';
import GuidedTakeReview from '@/routes/guided-recording/components/GuidedTakeReview.vue';
import { createGuidedMediaDevices } from '@/routes/guided-recording/media/GuidedMediaDevices.js';

const ACTIVE_SESSION_STATE = 'active';
const COMPLETED_SESSION_STATE = 'completed';
const QUESTION_STATES = ['pending', 'skipped', 'completed'];
const GUIDED_RECORDING_MINIMUM_DURATION_SECONDS = 5;
const GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS = 600;
const GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS = 1;
const RECORDER_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const isUploadCancelled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const isPublicUnavailableResponse = (error) => [403, 404].includes(error?.response?.status);

const INVALID_MULTIPART_RESPONSE_MESSAGE = 'Invalid guided multipart upload response.';

const isInvalidMultipartResponseError = (error) => error?.message === INVALID_MULTIPART_RESPONSE_MESSAGE;

const isInvalidSessionProjectionError = (error) => (
  typeof error?.message === 'string'
  && error.message.startsWith('Invalid guided-recording')
);

const getTakeExtension = (mimeType) => (mimeType.includes('mp4') ? 'mp4' : 'webm');

const UUID_PATTERN = /^[a-f\d]{8}-[a-f\d]{4}-[1-5][a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i;

const isValidUuid = (value) => typeof value === 'string' && UUID_PATTERN.test(value);

const isSafeServerIdentifier = (value) => (
  typeof value === 'string'
  && value.length > 0
  && value.trim() === value
);

const isValidPresignedUrl = (value) => {
  if (typeof value !== 'string' || !value) {
    return false;
  }

  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const createSecureUuid = () => {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : null;

  try {
    if (typeof cryptoApi?.randomUUID === 'function') {
      const uuid = cryptoApi.randomUUID();
      if (isValidUuid(uuid)) {
        return uuid;
      }
    }
  } catch {
    // Fall through to the secure random-values implementation.
  }

  try {
    if (typeof cryptoApi?.getRandomValues !== 'function') {
      return null;
    }

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const uuid = [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');

    return isValidUuid(uuid) ? uuid : null;
  } catch {
    return null;
  }
};

const isQuestionState = (state) => QUESTION_STATES.includes(state);


const isValidEffectiveDuration = (durationSeconds) => (
  Number.isInteger(durationSeconds)
  && durationSeconds >= GUIDED_RECORDING_MINIMUM_DURATION_SECONDS
  && durationSeconds <= GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS
);

const isValidRecordedTakeDuration = (take, maximumDurationSeconds) => {
  if (!isValidEffectiveDuration(maximumDurationSeconds)
    || take?.durationValid !== true
    || typeof take?.automaticStop !== 'boolean'
    || take.invalidReason
    || typeof take?.measuredDurationSeconds !== 'number'
    || !Number.isFinite(take.measuredDurationSeconds)
    || take.measuredDurationSeconds < 0
  ) {
    return false;
  }

  const tolerance = take.automaticStop ? GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS : 0;
  return take.measuredDurationSeconds <= maximumDurationSeconds + tolerance;
};

const isValidRecordedTake = (take, maximumDurationSeconds) => {
  const hasBlob = typeof Blob !== 'undefined'
    && take?.blob instanceof Blob
    && take.blob.size > 0;

  return hasBlob
    && RECORDER_MIME_TYPES.includes(take.mimeType)
    && isValidRecordedTakeDuration(take, maximumDurationSeconds);
};

const toSafeProgressQuestion = (question) => {
  if (
    !Number.isInteger(question?.order)
    || question.order < 1
    || typeof question.required !== 'boolean'
    || !isQuestionState(question.state)
  ) {
    return null;
  }

  return {
    order: question.order,
    required: question.required,
    state: question.state,
  };
};

const toSafeSessionProjection = (session) => {
  if (!session || ![ACTIVE_SESSION_STATE, COMPLETED_SESSION_STATE].includes(session.state)) {
    throw new Error('Invalid guided-recording session response.');
  }


  const questions = Array.isArray(session.questions)
    ? session.questions.map(toSafeProgressQuestion)
    : [];
  const orderedQuestions = [...questions].sort(
    (first, second) => (first?.order || 0) - (second?.order || 0),
  );
  const hasExpectedOrder = orderedQuestions.length > 0 && orderedQuestions.every(
    (question, index) => question && question.order === index + 1,
  );

  if (!hasExpectedOrder) {
    throw new Error('Invalid guided-recording question progress.');
  }

  if (session.state === COMPLETED_SESSION_STATE) {
    return {
      state: session.state,
      title: session.title,
      introduction: session.introduction,
      instructions: session.instructions,
      questions: orderedQuestions,
      currentQuestion: null,
    };
  }

  const currentQuestion = session.currentQuestion;
  const progressQuestion = orderedQuestions.find(({ order }) => order === currentQuestion?.order);
  if (
    !progressQuestion
    || typeof currentQuestion?.text !== 'string'
    || !currentQuestion.text.trim()
    || (typeof currentQuestion?.note !== 'string' && currentQuestion?.note !== null)
    || (typeof currentQuestion?.script !== 'string' && currentQuestion?.script !== null)
    || !isValidEffectiveDuration(currentQuestion?.maximumDurationSeconds)
  ) {
    throw new Error('Invalid current guided-recording question.');
  }

  return {
    state: session.state,
    title: session.title,
    introduction: session.introduction,
    instructions: session.instructions,
    questions: orderedQuestions,
    currentQuestion: {
      ...progressQuestion,
      text: currentQuestion.text,
      note: currentQuestion.note,
      script: currentQuestion.script,
      maximumDurationSeconds: currentQuestion.maximumDurationSeconds,
    },
  };
};

const GUIDED_RECORDING_RECOVERY_STORAGE_KEY = 'guidedRecordingRecovery';
const GUIDED_RECORDING_AUDIO_STATUSES = ['pending', 'pass', 'warning', 'fail', 'unavailable'];
const GUIDED_RECORDING_AUDIO_PROJECTION_FAILURE_STATUS = 'projection-failure';
const GUIDED_RECORDING_AUDIO_GUIDANCE = Object.freeze({
  fail: 'guidanceFailed',
  pass: 'guidancePass',
  pending: 'guidancePending',
  unavailable: 'guidanceUnavailable',
  warning: 'guidanceWarning',
});
const GUIDED_RECORDING_AUDIO_STATUS_POLL_INTERVAL_MS = 2000;
const GUIDED_RECORDING_UPLOAD_STATES = [
  'not_started',
  'local_take_not_durable',
  'upload_in_progress',
  'confirmed',
  'failed',
  'audio_pending',
  'audio_warning',
  'audio_failed',
  'audio_unavailable',
];
const GUIDED_RECORDING_ANSWER_UPLOAD_STATES = Object.freeze({
  created: 'local_take_not_durable',
  uploading: 'upload_in_progress',
  uploaded: 'upload_in_progress',
  registering: 'upload_in_progress',
  completed: 'confirmed',
  failed: 'failed',
  cancelled: 'failed',
});

const toSafeUploadState = (upload) => {
  const state = upload?.uploadState ?? upload?.state;
  if (GUIDED_RECORDING_UPLOAD_STATES.includes(state)) {
    return state;
  }

  return GUIDED_RECORDING_ANSWER_UPLOAD_STATES[state] || null;
};

const toSafeAudioResult = (audio) => {
  if (!audio || typeof audio !== 'object'
    || !GUIDED_RECORDING_AUDIO_STATUSES.includes(audio.status)
    || audio.guidance !== GUIDED_RECORDING_AUDIO_GUIDANCE[audio.status]
    || typeof audio.resolved !== 'boolean'
    || !Array.isArray(audio.issueCodes)
    || !audio.issueCodes.every((issueCode) => typeof issueCode === 'string')
  ) {
    // Keep an invalid server projection visible rather than reverting to the
    // recorder. This local status is never treated as a server verdict.
    return {
      resolved: false,
      status: GUIDED_RECORDING_AUDIO_PROJECTION_FAILURE_STATUS,
    };
  }

  return {
    resolved: audio.resolved,
    status: audio.status,
  };
};

const readRecoveryStore = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(GUIDED_RECORDING_RECOVERY_STORAGE_KEY);
    const store = raw ? JSON.parse(raw) : {};
    return store && typeof store === 'object' ? store : {};
  } catch {
    return {};
  }
};

const writeRecoveryStore = (store) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (Object.keys(store).length === 0) {
      window.localStorage.removeItem(GUIDED_RECORDING_RECOVERY_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(GUIDED_RECORDING_RECOVERY_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage may be unavailable (private mode, quota); recovery is best-effort.
  }
};

const toSafeRecoveryData = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  if (!isValidUuid(entry.browserContextId)) {
    return null;
  }
  if (typeof entry.recoveryCredential !== 'string' || !entry.recoveryCredential) {
    return null;
  }

  return {
    browserContextId: entry.browserContextId,
    recoveryCredential: entry.recoveryCredential,
    sessionCapability: typeof entry.sessionCapability === 'string' ? entry.sessionCapability : '',
  };
};

const getRecoveryData = (code) => toSafeRecoveryData(readRecoveryStore()[code]);

const saveRecoveryData = (code, recoveryData) => {
  const store = readRecoveryStore();
  store[code] = recoveryData;
  writeRecoveryStore(store);
};

const clearRecoveryData = (code) => {
  const store = readRecoveryStore();
  if (!Object.prototype.hasOwnProperty.call(store, code)) {
    return;
  }
  delete store[code];
  writeRecoveryStore(store);
};

const toSafeAudioGateCompletion = (completion) => {
  if (!completion || typeof completion !== 'object'
    || typeof completion.podid !== 'string' || !completion.podid
    || typeof completion.uploadid !== 'string' || !completion.uploadid
    || !Array.isArray(completion.uploadedparts)
    || completion.uploadedparts.length === 0
    || !completion.uploadedparts.every((part) => part && typeof part === 'object'
      && Number.isInteger(part.PartNumber)
      && typeof part.ETag === 'string' && part.ETag)) {
    return null;
  }

  return {
    podid: completion.podid,
    uploadid: completion.uploadid,
    uploadedparts: completion.uploadedparts.map(({ ETag, PartNumber }) => ({ ETag, PartNumber })),
  };
};

// The multipart-completion payload is required to re-drive finalization once
// a gated take's audio result arrives (pass or an override/continue). It is
// persisted alongside the recovery data so a page reload can still complete
// the take instead of leaving the contributor on a dead pass panel.
const getAudioGateCompletion = (code) => toSafeAudioGateCompletion(
  readRecoveryStore()[code]?.audioGateCompletion
);

const saveAudioGateCompletion = (code, completion) => {
  const store = readRecoveryStore();
  const entry = store[code];
  if (!entry || typeof entry !== 'object') {
    return;
  }

  const safe = toSafeAudioGateCompletion(completion);
  if (safe) {
    store[code] = { ...entry, audioGateCompletion: safe };
  } else if (Object.prototype.hasOwnProperty.call(entry, 'audioGateCompletion')) {
    const rest = { ...entry };
    delete rest.audioGateCompletion;
    store[code] = rest;
  }
  writeRecoveryStore(store);
};

const toSafeAttestation = (attestation) => {
  if (!attestation || typeof attestation !== 'object') {
    return null;
  }
  if (typeof attestation.participantDisplayName !== 'string'
    || !attestation.participantDisplayName.trim()) {
    return null;
  }

  return {
    participantDisplayName: attestation.participantDisplayName,
    consentAccepted: attestation.consentAccepted === true,
    consentStatementVersion: typeof attestation.consentStatementVersion === 'string'
      ? attestation.consentStatementVersion
      : null,
  };
};

const toSafeUploadReferences = (uploads) => {
  if (!Array.isArray(uploads)) {
    return null;
  }
  const references = [];
  const questionOrders = new Set();
  for (const upload of uploads) {
    const audio = upload?.audio === undefined ? null : toSafeAudioResult(upload.audio);
    const uploadState = toSafeUploadState(upload)
      || (audio?.status === GUIDED_RECORDING_AUDIO_PROJECTION_FAILURE_STATUS
        ? 'audio_unavailable'
        : null);
    if (!upload
      || !Number.isInteger(upload.questionOrder)
      || upload.questionOrder < 1
      || questionOrders.has(upload.questionOrder)
      || !uploadState
      || (upload.audio !== undefined && !audio)) {
      return null;
    }
    questionOrders.add(upload.questionOrder);
    references.push({
      questionOrder: upload.questionOrder,
      uploadState,
      ...(typeof upload.uploadId === 'string' && upload.uploadId ? { uploadId: upload.uploadId } : {}),
      ...(typeof upload.podId === 'string' && upload.podId ? { podId: upload.podId } : {}),
      ...(typeof upload.fileUuid === 'string' && upload.fileUuid ? { fileUuid: upload.fileUuid } : {}),
      ...(audio ? { audio } : {}),
    });
  }
  return references;
};

export default {
  name: 'GuidedRecordingPublic',
  components: {
    GuidedAudioResultPanel,
    GuidedAutocue,
    GuidedRecorder,
    GuidedTakeReview,
    MdPreview,
  },
  data() {
    return {
      audioAction: '',
      audioGateCompletion: null,
      audioStatusPollTimer: null,
      invitationExpiryTimer: null,
      autocueAlignment: 'center',
      autocueFontSize: 24,
      autocueScrollSpeed: 40,
      consentAccepted: false,
      consentError: '',
       invitation: null,
       isMultipartCompleted: false,
        isAudioActionInProgress: false,
       isCancelling: false,
       isLoading: true,
       isRetryableSubmissionFailure: false,
       isSessionActionInProgress: false,
      isStartingSession: false,
      recordingPageEntered: false,
      isSubmitting: false,
      isUnavailable: false,
      invitationLoadFailed: false,
      participantDisplayName: '',
      participantNameError: '',
      recorderElapsedSeconds: 0,
      recorderIsRecording: false,
      recorderRemainingSeconds: 0,
      recoveryAttestation: null,
      recoveryData: null,
      recoveryState: null,
      recoveryUploads: [],
      sessionAction: '',
      sessionCapability: '',
      sessionError: '',
      sessionFreshness: 0,
      sessionProjection: null,
      sessionStatus: '',
      submissionError: '',
      submissionProgress: 0,
      theme: null,
      draftTakesByQuestion: {},
      draftSessionIdentity: createSecureUuid(),
      mediaDevicesController: null,
      mediaGeneration: 0,
      sourceStream: null,
      videoDevices: [],
      audioDevices: [],
      selectedVideoDeviceId: '',
      selectedAudioDeviceId: '',
      sourceRequesting: false,
      previewAuthorized: false,
      sourceErrorKey: '',
      noiseSuppression: false,
      noiseSuppressionSupported: false,
      noiseSuppressionApplied: null,
      blurEnabled: false,
      blurSupported: true,
      blurState: 'idle',
      blurReason: null,
      recorderBusy: false,
      layoutRuntime: markRaw({ observer: null, frame: null }),
      uploadAbortController: null,
      uploadDetails: null,
    };
  },
  computed: {
    isRecordingPage() {
      return this.recordingPageEntered && Boolean(this.sessionProjection);
    },
    currentDraftKey() {
      return this.currentQuestion
        ? `${this.draftSessionIdentity}:${this.currentQuestion.order}` : '';
    },
    take: {
      get() {
        return this.draftTakesByQuestion[this.currentDraftKey]?.take || null;
      },
      set(take) {
        if (!this.currentDraftKey) return;
        const entry = this.draftTakesByQuestion[this.currentDraftKey] || {};
        this.draftTakesByQuestion[this.currentDraftKey] = { ...entry, take };
      },
    },
    takeIdempotencyKey: {
      get() {
        return this.draftTakesByQuestion[this.currentDraftKey]?.idempotencyKey || '';
      },
      set(idempotencyKey) {
        if (!this.currentDraftKey) return;
        const entry = this.draftTakesByQuestion[this.currentDraftKey] || {};
        this.draftTakesByQuestion[this.currentDraftKey] = { ...entry, idempotencyKey };
      },
    },
    devicesDisabled() {
      return this.sourceRequesting || this.recorderBusy || this.isSessionActionDisabled;
    },
    getDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
    pageTheme() {
      const brandTheme = this.invitation?.brand?.theme;
      return brandTheme === 'dark' || brandTheme === 'light'
        ? brandTheme
        : (this.getDarkMode ? 'dark' : 'light');
    },
    getExpireAt() {
      return this.invitation?.expireat
        || this.invitation?.expiresAt
        || this.invitation?.expiresat
        || '';
    },
    getExpiryDays() {
      const expireAt = new Date(this.getExpireAt);
      return Number.isNaN(expireAt.getTime())
        ? ''
        : Math.floor((expireAt - new Date()) / 86400000);
    },
    canGoNext() {
      return this.isSessionActive
        && this.currentQuestion.order < this.sessionQuestions.length
        && !this.isSessionActionDisabled;
    },
    consentRequired() {
      return this.invitation?.consent?.required === true;
    },
    canGoPrevious() {
      return this.isSessionActive
        && this.currentQuestion.order > 1
        && !this.isSessionActionDisabled;
    },
    canSkip() {
      return this.isSessionActive
        && !this.currentQuestion.required
        && this.currentQuestion.state === 'pending';
    },
    currentQuestion() {
      return this.sessionProjection?.currentQuestion || null;
    },
    currentQuestionHeadingId() {
      return 'guided-recording-public-current-question';
    },
    currentQuestionAudioResult() {
      const upload = this.recoveryUploads.find(
        ({ questionOrder }) => questionOrder === this.currentQuestion?.order,
      );
      return upload?.audio || null;
    },
    isTakeProcessing() {
      return this.isSessionActive
        && !this.currentQuestionAudioResult
        && this.currentQuestionUploadState() === 'upload_in_progress'
        && Boolean(this.getUsableAudioGateCompletion(this.captureSessionContext()));
    },
    isSessionActionDisabled() {
      return this.recorderBusy || this.sourceRequesting || this.isAudioActionInProgress
        || this.isSessionActionInProgress || this.isSubmitting || Boolean(this.uploadDetails)
        || Boolean(this.audioGateCompletion)
        || Boolean(this.currentQuestionAudioResult && this.currentQuestion?.state !== 'completed');
    },
    isSessionActive() {
      return this.recordingPageEntered
        && this.sessionProjection?.state === ACTIVE_SESSION_STATE && Boolean(this.currentQuestion);
    },
    isSubmitted() {
      return this.sessionProjection?.state === COMPLETED_SESSION_STATE;
    },
    progressHeadingId() {
      return 'guided-recording-public-progress';
    },
    progressPercentage() {
      if (!this.sessionQuestions.length) {
        return 0;
      }

      return Math.round((this.resolvedQuestionCount / this.sessionQuestions.length) * 100);
    },
    questionComponentRecorderKey() {
      return `guided-recording-question-recorder-${this.currentQuestion.order}`;
    },
    questionComponentReviewKey() {
      return `guided-recording-question-review-${this.currentQuestion.order}`;
    },
    resolvedQuestionCount() {
      return this.sessionQuestions.filter(({ state }) => state !== 'pending').length;
    },
    sessionQuestions() {
      return this.sessionProjection?.questions || [];
    },
    displayedConfiguration() {
      return (this.isRecordingPage ? this.sessionProjection : this.invitation) || {};
    },
    isRecoveryInProgress() {
      return this.recoveryState === 'checking' || this.recoveryState === 'resuming';
    },
    recoveryBannerText() {
      if (this.isRecoveryInProgress) {
        return this.$t('routes.guidedRecording.recovery.resuming');
      }
      if (this.recoveryState === 'resumed' && this.recoveryAttestation) {
        return this.$t('routes.guidedRecording.recovery.resumingAs', {
          name: this.recoveryAttestation.participantDisplayName,
        });
      }
      return '';
    },
    currentQuestionTakeNotDurable() {
      if (
        this.recoveryState !== 'resumed'
        || !this.currentQuestion
        || this.currentQuestion.state === 'completed'
      ) {
        return false;
      }
      const upload = this.recoveryUploads.find(
        ({ questionOrder }) => questionOrder === this.currentQuestion.order,
      );
      return upload?.uploadState === 'local_take_not_durable';
    },
  },
  watch: {
    pageTheme(themeName) {
      if (this.theme) {
        this.theme.change(themeName);
      }
    },
    async currentDraftKey() {
      const context = this.captureSessionContext();
      await this.$nextTick();
      if (!this.isSessionContextCurrent(context) || !this.isSessionActive) return;
      this.initializeDevices();
      if (this.take || this.currentQuestion.state === 'completed' || this.currentQuestionAudioResult) {
        this.releaseSource();
      } else if (!this.currentQuestionAudioResult && !this.sourceStream && !this.sourceErrorKey) {
        this.requestPreview();
      }
    },
    '$route.params.code': {
      handler() {
        this.loadInvitation();
      },
      immediate: true,
    },
    recoveryState(state) {
      if (['checking', 'resuming', 'failed'].includes(state)) this.releaseSource();
      else if (state === 'resumed' && !this.take && !this.currentQuestionAudioResult
        && this.currentQuestion?.state !== 'completed' && !this.sourceErrorKey) this.requestPreview();
    },
    currentQuestionAudioResult: {
      handler(result, previous) {
        if (result) this.releaseSource();
        if (previous?.status !== 'pass' && result?.status === 'pass') {
          this.completeArrivedAudioResult();
        }
      },
    },
  },
  mounted() {
    this.theme = useTheme();
    this.theme.change(this.pageTheme);
    document.addEventListener('visibilitychange', this.scheduleInvitationExpiry);
    window.addEventListener('focus', this.scheduleInvitationExpiry);
    window.addEventListener('pageshow', this.scheduleInvitationExpiry);
    this.layoutRuntime.observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(this.schedulePreviewLayout) : null;
    window.addEventListener('resize', this.schedulePreviewLayout);
    this.observePreviewLayout();
  },
  updated() {
    this.observePreviewLayout();
  },
  beforeUnmount() {
    this.layoutRuntime.observer?.disconnect();
    window.removeEventListener('resize', this.schedulePreviewLayout);
    if (this.layoutRuntime.frame !== null) cancelAnimationFrame(this.layoutRuntime.frame);
    this.clearInvitationExpiryTimer();
    document.removeEventListener('visibilitychange', this.scheduleInvitationExpiry);
    window.removeEventListener('focus', this.scheduleInvitationExpiry);
    window.removeEventListener('pageshow', this.scheduleInvitationExpiry);
    this.invalidateSessionContext();
    this.stopAudioStatusPolling();
    this.releaseLocalResources(true);
    if (typeof window !== 'undefined') {
      window.onbeforeunload = null;
    }
  },
  methods: {
    isoToDate,
    observePreviewLayout() {
      const workspace = this.$refs.workspace?.$el;
      this.layoutRuntime.observer?.disconnect();
      if (!workspace) return;
      this.layoutRuntime.observer?.observe(workspace);
      workspace.querySelectorAll('.guided-recording-public__current-question, .guided-recorder__actions, .guided-autocue, [role="status"]')
        .forEach((element) => this.layoutRuntime.observer?.observe(element));
      this.schedulePreviewLayout();
    },
    schedulePreviewLayout() {
      if (this.layoutRuntime.frame !== null) return;
      this.layoutRuntime.frame = requestAnimationFrame(() => {
        this.layoutRuntime.frame = null;
        const workspace = this.$refs.workspace?.$el;
        const preview = workspace?.querySelector('.guided-recorder__preview');
        const recorder = workspace?.querySelector('.guided-recorder');
        if (!preview || !recorder) return;
        const bounds = preview.getBoundingClientRect();
        const controlsHeight = recorder.getBoundingClientRect().bottom - bounds.bottom;
        // A minimum useful preview permits document scrolling for long text/zoom.
        const height = Math.max(180, Math.floor(window.innerHeight - bounds.top - controlsHeight - 20));
        workspace.style.setProperty('--guided-preview-max-height', `${height}px`);
      });
    },
    initializeDevices() {
      if (this.mediaDevicesController) return;
      const generation = ++this.mediaGeneration;
      const supported = navigator.mediaDevices?.getSupportedConstraints?.() || {};
      this.noiseSuppressionSupported = supported.noiseSuppression === true;
      this.noiseSuppression = this.noiseSuppressionSupported;
      this.mediaDevicesController = markRaw(createGuidedMediaDevices({
        onState: (state) => {
          if (generation !== this.mediaGeneration) return;
          this.sourceStream = state.stream ? markRaw(state.stream) : null;
          this.videoDevices = state.videoDevices.map((device) => ({
            ...device,
            label: device.label || `${this.$t(`routes.guidedRecording.${device.fallbackKey}`)} ${device.index}`,
          }));
          this.audioDevices = state.audioDevices.map((device) => ({
            ...device,
            label: device.label || `${this.$t(`routes.guidedRecording.${device.fallbackKey}`)} ${device.index}`,
          }));
          this.selectedVideoDeviceId = state.selectedVideoDeviceId;
          this.selectedAudioDeviceId = state.selectedAudioDeviceId;
          this.sourceRequesting = state.requesting;
          this.sourceErrorKey = state.errorKey;
          this.noiseSuppressionSupported = state.noiseSuppressionSupported;
          this.noiseSuppressionApplied = state.noiseSuppressionApplied;
        },
      }));
      this.mediaDevicesController.refreshDevices();
    },
    enablePreview() {
      if (!this.isSessionActive || this.isSessionActionDisabled) return;
      this.previewAuthorized = true;
      return this.requestPreview();
    },
    async requestPreview() {
      if (!this.previewAuthorized || !this.isSessionActive || !this.invitation || this.isUnavailable || this.isSubmitted
        || this.isRecoveryInProgress || this.recoveryState === 'failed' || this.take
        || this.currentQuestionAudioResult || this.currentQuestion?.state === 'completed'
        || !this.scheduleInvitationExpiry() || this.sourceRequesting || this.recorderBusy) return;
      this.initializeDevices();
      await this.mediaDevicesController.start({
        videoDeviceId: this.selectedVideoDeviceId,
        audioDeviceId: this.selectedAudioDeviceId,
        noiseSuppression: this.noiseSuppression,
      });
    },
    releaseSource() {
      this.mediaDevicesController?.stop();
    },
    disposeDevices() {
      this.mediaGeneration += 1;
      this.mediaDevicesController?.dispose();
      this.mediaDevicesController = null;
      this.sourceStream = null;
      this.previewAuthorized = false;
      this.sourceRequesting = false;
      this.recorderBusy = false;
      this.sourceErrorKey = '';
      this.blurEnabled = false;
      this.blurSupported = true;
      this.blurState = 'idle';
      this.blurReason = null;
    },
    changeDevicePreference() {
      if (this.sourceStream && !this.devicesDisabled) this.requestPreview();
    },
    handleRecorderBusy(busy) {
      this.recorderBusy = busy;
      this.updateNavigationWarning();
    },
    handleRecorderCapability({ blurSupported, blurState, blurReason = null }) {
      this.blurSupported = blurSupported;
      this.blurState = blurState;
      this.blurReason = blurReason;
    },
    clearInvitationExpiryTimer() {
      if (this.invitationExpiryTimer !== null) {
        window.clearTimeout(this.invitationExpiryTimer);
        this.invitationExpiryTimer = null;
      }
    },
    hasFutureInvitationExpiry() {
      const expiresAt = this.getExpireAt;
      return Boolean(expiresAt) && new Date(expiresAt).getTime() > Date.now();
    },
    scheduleInvitationExpiry() {
      this.clearInvitationExpiryTimer();
      if (!this.invitation) {
        return false;
      }
      if (!this.hasFutureInvitationExpiry()) {
        this.markUnavailable();
        return false;
      }
      // Long-lived invitations must not overflow the browser's signed 32-bit delay.
      const delay = Math.min(new Date(this.getExpireAt).getTime() - Date.now(), 2147483647);
      const invitation = this.invitation;
      const code = this.$route.params.code;
      this.invitationExpiryTimer = window.setTimeout(() => {
        if (this.invitation === invitation && this.$route.params.code === code) {
          this.scheduleInvitationExpiry();
        }
      }, delay);
      return true;
    },
    async acceptTake() {
      if (
        !isValidRecordedTake(
          this.take,
          this.currentQuestion?.maximumDurationSeconds,
        )
        || typeof this.take?.url !== 'string'
        || !this.take.url
        || !this.isSessionActive
        || this.isSubmitting
        || this.uploadDetails
      ) {
        if (this.take) {
          this.submissionError = this.$t('routes.guidedRecording.invalidTakeDuration');
        }
        return;
      }

      const sessionContext = this.captureSessionContext();
      const uploadAbortController = new AbortController();
      this.audioGateCompletion = null;
      this.isMultipartCompleted = false;
      saveAudioGateCompletion(sessionContext.code, null);
      this.isSubmitting = true;
      this.isRetryableSubmissionFailure = false;
      this.isCancelling = false;
      this.submissionError = '';
      this.submissionProgress = 0;
      this.uploadAbortController = uploadAbortController;
      this.updateNavigationWarning();
      await this.runTakeUpload(sessionContext, uploadAbortController, { reuseUpload: false });
    },
    async overrideAudioWarning() {
      return this.resolveAudioResult(
        'warning',
        'overrideAudioWarning',
        'warning-override',
      );
    },
    async continueAudioUnavailable() {
      return this.resolveAudioResult(
        'unavailable',
        'continueAudioUnavailable',
        'unavailable-continue',
      );
    },
    async resolveAudioResult(expectedStatus, method, action) {
      if (
        !this.isSessionActive
        || this.isAudioActionInProgress
        || this.currentQuestionAudioResult?.status !== expectedStatus
        || this.currentQuestionAudioResult.resolved
      ) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.audioAction = action;
      this.isAudioActionInProgress = true;
      this.sessionError = '';
      try {
        await Api.guidedRecording[method](sessionContext.code, sessionContext.sessionCapability);
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        await this.redriveAudioGateCompletion(sessionContext);
      } catch (error) {
        await this.handleSessionRequestError(error, sessionContext);
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.audioAction = '';
          this.isAudioActionInProgress = false;
        }
      }
    },
    getUsableAudioGateCompletion(sessionContext) {
      // The in-memory payload has a strict lifecycle (cleared on take start
      // and question changes), so it is always trusted.
      if (this.audioGateCompletion) {
        return this.audioGateCompletion;
      }

      // Persisted payloads (page reload) are only trusted while they still
      // match the current question's registered upload identity, so a stale
      // payload can never target a replaced or already-completed take.
      const completion = getAudioGateCompletion(sessionContext.code);
      if (!completion) {
        return null;
      }
      const upload = this.recoveryUploads.find(
        ({ questionOrder }) => questionOrder === this.currentQuestion?.order,
      );
      if (!upload
        || upload.uploadId !== completion.uploadid
        || upload.podId !== completion.podid) {
        return null;
      }

      return completion;
    },
    async redriveAudioGateCompletion(sessionContext) {
      const completion = this.getUsableAudioGateCompletion(sessionContext);
      if (!completion) {
        const refreshed = await this.refreshSession({ focusCurrent: true });
        if (this.isSessionContextCurrent(sessionContext) && this.currentQuestionAudioResult) {
          this.sessionError = this.$t('routes.guidedRecording.finalizationRecoveryUnavailable');
        }
        return refreshed;
      }

      await Api.guidedRecording.completeAcceptedTake(
        completion,
        sessionContext.sessionCapability,
      );
      if (!this.isSessionContextCurrent(sessionContext)) {
        return false;
      }

      return this.refreshSession({ focusCurrent: true });
    },
    async completeArrivedAudioResult() {
      if (!this.isSessionActive
        || this.isAudioActionInProgress
        || this.isSubmitting) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.audioAction = 'pass-completion';
      this.isAudioActionInProgress = true;
      this.sessionError = '';
      try {
        await this.redriveAudioGateCompletion(sessionContext);
      } catch (error) {
        await this.handleSessionRequestError(error, sessionContext);
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.audioAction = '';
          this.isAudioActionInProgress = false;
        }
      }
    },
    async recordAudioAgain() {
      const status = this.currentQuestionAudioResult?.status;
      if (
        !this.isSessionActive
        || this.isAudioActionInProgress
        || this.currentQuestionAudioResult?.resolved
        || !['fail', 'unavailable', 'warning'].includes(status)
      ) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      const questionOrder = this.currentQuestion.order;
      this.audioAction = 'record-again';
      this.isAudioActionInProgress = true;
      this.sessionError = '';
      try {
        const replacement = await Api.guidedRecording.recordAudioAgain(
          sessionContext.code,
          sessionContext.sessionCapability,
        );
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        if (
          !isSafeServerIdentifier(replacement?.id)
          || replacement.state !== 'created'
          || replacement.questionOrder !== questionOrder
          || !isValidUuid(replacement.idempotencyKey)
        ) {
          throw new Error('Invalid guided-recording replacement response.');
        }

        // The record-again endpoint atomically tombstones the old source and
        // provisions the replacement, so only stale local state is cleared.
        this.clearQuestionResources();
        this.takeIdempotencyKey = replacement.idempotencyKey;
        this.markCurrentQuestionForRerecord();
        await this.$nextTick();
        await this.requestPreview();
        await this.refreshSession({ focusCurrent: true });
      } catch (error) {
        await this.handleSessionRequestError(error, sessionContext);
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.audioAction = '';
          this.isAudioActionInProgress = false;
        }
      }
    },
    async retryAudioResultProjection() {
      if (
        !this.isSessionActive
        || this.isAudioActionInProgress
        || this.currentQuestionAudioResult?.status
          !== GUIDED_RECORDING_AUDIO_PROJECTION_FAILURE_STATUS
      ) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.audioAction = 'projection-retry';
      this.isAudioActionInProgress = true;
      this.sessionError = '';
      try {
        await this.refreshUploadStatus(sessionContext);
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.audioAction = '';
          this.isAudioActionInProgress = false;
        }
      }
    },
    async retryUpload() {
      if (!this.take || this.isSubmitting) {
        return;
      }
      const sessionContext = this.captureSessionContext();
      if (this.uploadDetails && !this.hasMultipartUploadInstructions()) {
        await this.cleanupMalformedMultipartResponse(sessionContext);
        return;
      }
      if (this.uploadDetails) {
        await this.refreshUploadStatus(sessionContext);
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
      }
      const uploadState = this.currentQuestionUploadState();
      if (uploadState === 'confirmed') {
        this.clearQuestionResources();
        await this.refreshSession({ focusCurrent: true });
        return;
      }
      const reuseUpload = Boolean(this.uploadDetails)
        && !['failed', 'local_take_not_durable'].includes(uploadState);
      if (!reuseUpload) {
        this.uploadDetails = null;
      }
      const uploadAbortController = new AbortController();
      this.isSubmitting = true;
      this.isRetryableSubmissionFailure = false;
      this.isCancelling = false;
      this.submissionError = '';
      this.submissionProgress = 0;
      this.uploadAbortController = uploadAbortController;
      this.updateNavigationWarning();
      await this.runTakeUpload(sessionContext, uploadAbortController, { reuseUpload });
    },
    async runTakeUpload(sessionContext, uploadAbortController, { reuseUpload }) {
      let isUploadLifecycleRequest = false;
      try {
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        if (!reuseUpload || !this.uploadDetails) {
          this.isMultipartCompleted = false;
          isUploadLifecycleRequest = true;
          const hasRecoveredLocalTake = this.currentQuestionUploadState()
            === 'local_take_not_durable';
          if (!this.takeIdempotencyKey && !hasRecoveredLocalTake) {
            await this.persistLocalTake(sessionContext);
          }
          if (!this.isSessionContextCurrent(sessionContext)) {
            return;
          }
          const createResponse = await Api.guidedRecording.createAcceptedTake(
            this.createUploadPayload(),
            sessionContext.sessionCapability,
            { signal: uploadAbortController.signal },
          );
          if (!this.isSessionContextCurrent(sessionContext)) {
            return;
          }
          isUploadLifecycleRequest = false;
          this.uploadDetails = this.getMultipartCleanupDetails(createResponse);
          this.uploadDetails = this.getMultipartDetails(createResponse);
        }
        if (this.isCancelling) {
          await this.cleanupCancelledUpload(sessionContext);
          return;
        }
        const uploadedparts = await this.uploadMultipartTake(sessionContext, uploadAbortController);
        if (!uploadedparts || !this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        isUploadLifecycleRequest = true;
        this.audioGateCompletion = {
          podid: this.uploadDetails.podId,
          uploadid: this.uploadDetails.uploadId,
          uploadedparts,
        };
        saveAudioGateCompletion(sessionContext.code, this.audioGateCompletion);
        await Api.guidedRecording.completeAcceptedTake(
          this.audioGateCompletion,
          sessionContext.sessionCapability,
        );
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        this.isMultipartCompleted = true;
        isUploadLifecycleRequest = false;

        this.submissionProgress = 100;
        await this.refreshSession({ focusCurrent: true });
      } catch (error) {
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        if (isGuidedRecordingFinalizationRetryable(error)) {
          this.uploadDetails = null;
          this.isRetryableSubmissionFailure = true;
          this.submissionProgress = 0;
          this.submissionError = this.$t('routes.guidedRecording.submissionFailed');
          await this.refreshUploadStatus(sessionContext);
        } else if (isInvalidMultipartResponseError(error) && this.uploadDetails) {
          await this.cleanupMalformedMultipartResponse(sessionContext);
        } else if (isUploadLifecycleRequest && isPublicUnavailableResponse(error)) {
          await this.markUnavailable(sessionContext);
        } else if (isUploadCancelled(error) && this.isCancelling && !this.uploadDetails) {
          this.clearTake();
          this.submissionProgress = 0;
          this.isCancelling = false;
          await this.refreshSession({ focusCurrent: true });
        } else if (!isUploadCancelled(error)) {
          this.submissionError = this.$t('routes.guidedRecording.submissionFailed');
          await this.refreshUploadStatus(sessionContext);
        }
      } finally {
        if (this.isSessionContextCurrent(sessionContext)
          && this.uploadAbortController === uploadAbortController) {
          this.isSubmitting = false;
          this.uploadAbortController = null;
          this.updateNavigationWarning();
          if (this.currentQuestionAudioResult?.status === 'pass'
            && this.currentQuestion?.state !== 'completed') {
            void this.completeArrivedAudioResult();
          }
        }
      }
    },
    applySessionProjection(session, sessionContext, { focusCurrent = false } = {}) {
      const projection = toSafeSessionProjection(session);
      if (!this.isSessionContextCurrent(sessionContext)) {
        return Promise.resolve(false);
      }
      const previousQuestionOrder = this.currentQuestion?.order;
      projection.questions.filter(({ state }) => state === 'completed').forEach(({ order }) => {
        this.clearDraft(`${this.draftSessionIdentity}:${order}`);
      });

      if (projection.state === COMPLETED_SESSION_STATE) {
        this.releaseLocalResources();
        this.sessionProjection = projection;
        this.sessionStatus = this.$t('routes.guidedRecording.sessionCompletedAnnouncement');
        this.clearRecoveryState();
        return Promise.resolve(true);
      }

      if (previousQuestionOrder && previousQuestionOrder !== projection.currentQuestion.order) {
        this.clearQuestionResources({ preserveDraft: true });
      }

      this.sessionProjection = projection;
      this.isUnavailable = false;
      this.sessionStatus = this.$t('routes.guidedRecording.questionStatusAnnouncement', {
        current: projection.currentQuestion.order,
        total: projection.questions.length,
        requirement: this.questionRequirementLabel(projection.currentQuestion),
        status: this.questionStateLabel(projection.currentQuestion.state),
      });

      return focusCurrent
        ? this.focusCurrentQuestion(sessionContext)
        : Promise.resolve(true);
    },
    async cancelUpload() {
      if (!this.isSubmitting && !this.uploadDetails) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.isCancelling = true;
      this.uploadAbortController?.abort();
      this.submissionError = '';
      if (!this.uploadDetails) {
        return;
      }

      try {
        await this.cleanupCancelledUpload(sessionContext);
      } catch {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.submissionError = this.$t('routes.guidedRecording.cancelSubmissionFailed');
          this.isCancelling = false;
        }
      }
    },
    getTrimmedParticipantDisplayName() {
      return this.participantDisplayName.trim();
    },
    focusInvalidSessionField() {
      if (this.participantNameError) {
        this.$refs.participantName?.focus();
        return;
      }

      const consent = this.$refs.consent;
      if (typeof consent?.focus === 'function') {
        consent.focus();
        return;
      }

      consent?.$el?.querySelector('input')?.focus();
    },
    async enterRecordingPage() {
      const sessionContext = this.captureSessionContext();
      this.recordingPageEntered = true;
      if (this.isSessionActive) this.initializeDevices();
      this.syncAudioStatusPolling();
      if (!await this.focusCurrentQuestion(sessionContext) || !this.isSessionActive) return;
      if (this.currentQuestionAudioResult?.status === 'pass') {
        await this.completeArrivedAudioResult();
      }
    },
    async startSession() {
      if (this.isRecoveryInProgress || this.isStartingSession) return;
      if (!this.scheduleInvitationExpiry()) {
        return;
      }
      this.sessionError = '';
      this.sessionStatus = '';
      if (!this.validateSession()) {
        this.$nextTick(() => this.focusInvalidSessionField());
        return;
      }
      if (this.sessionCapability && this.sessionProjection) {
        await this.enterRecordingPage();
        return;
      }

      let sessionContext = this.captureSessionContext();
      this.isStartingSession = true;
      try {
        const browserContextId = createSecureUuid();
        const sessionData = {
          participantDisplayName: this.getTrimmedParticipantDisplayName(),
          consentAccepted: this.consentRequired ? this.consentAccepted : false,
        };
        const session = browserContextId
          ? await Api.guidedRecording.startPublicSession(
            sessionContext.code,
            sessionData,
            browserContextId,
          )
          : await Api.guidedRecording.startPublicSession(sessionContext.code, sessionData);
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        if (!session?.sessionCapability) {
          throw new Error('Missing guided recording session capability.');
        }
        this.setSessionCapability(session.sessionCapability);
        sessionContext = this.captureSessionContext();
        if (
          isValidUuid(browserContextId)
          && typeof session.recoveryCredential === 'string'
          && session.recoveryCredential
        ) {
          saveRecoveryData(sessionContext.code, {
            browserContextId,
            recoveryCredential: session.recoveryCredential,
            sessionCapability: session.sessionCapability,
          });
        }
        const currentContext = this.captureSessionContext();
        const applied = await this.refreshSession();
        if (applied && this.isSessionContextCurrent(currentContext)) {
          await this.enterRecordingPage();
        }
      } catch (error) {
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        if (isPublicUnavailableResponse(error)) {
          await this.markUnavailable(sessionContext);
        } else {
          this.sessionError = this.$t('routes.guidedRecording.sessionStartError');
        }
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.isStartingSession = false;
        }
      }
    },
    validateSession() {
      this.participantNameError = '';
      this.consentError = '';
      const participantDisplayName = this.getTrimmedParticipantDisplayName();
      if (!participantDisplayName) {
        this.participantNameError = this.$t('routes.guidedRecording.participantNameRequired');
      } else if (participantDisplayName.length > 30) {
        this.participantNameError = this.$t('routes.guidedRecording.participantNameLength');
      }
      if (this.consentRequired && !this.consentAccepted) {
        this.consentError = this.$t('routes.guidedRecording.consentRequired');
      }

      return !this.participantNameError && !this.consentError;
    },
    clearQuestionResources({ preserveDraft = false } = {}) {
      this.stopAudioStatusPolling();
      this.audioGateCompletion = null;
      this.isMultipartCompleted = false;
      saveAudioGateCompletion(this.$route.params.code, null);
      if (!preserveDraft) this.clearTake();
      this.recorderElapsedSeconds = 0;
      this.recorderIsRecording = false;
      this.recorderRemainingSeconds = 0;
      this.submissionError = '';
      this.submissionProgress = 0;
      this.uploadDetails = null;
      this.isRetryableSubmissionFailure = false;
      this.updateNavigationWarning();
    },
    clearRecoveryState() {
      clearRecoveryData(this.$route.params.code);
      this.recoveryAttestation = null;
      this.recoveryData = null;
      this.recoveryState = null;
      this.recoveryUploads = [];
    },
    async retrySession() {
      if (this.recoveryState === 'failed' && this.recoveryData) {
        return this.loadInvitation();
      }

      return this.refreshSession({ focusCurrent: true });
    },
    handleAutocueTelemetry(payload) {
      const sessionContext = this.captureSessionContext();
      if (!sessionContext.code || !sessionContext.sessionCapability) {
        return;
      }

      try {
        const request = Api.guidedRecording.recordAutocueTelemetry(
          sessionContext.code,
          sessionContext.sessionCapability,
          payload?.event,
          payload?.reason,
        );
        request?.catch?.(() => undefined);
      } catch {
        // Autocue telemetry is best-effort and must never affect recording.
      }
    },
    handleTimerTick({ elapsedSeconds, remainingSeconds }) {
      if (!this.isSessionContextCurrent(this.captureSessionContext())) {
        return;
      }
      this.recorderElapsedSeconds = elapsedSeconds;
      this.recorderRemainingSeconds = remainingSeconds;
    },
    handleIsRecording(isRecording) {
      this.recorderIsRecording = isRecording;
      this.updateNavigationWarning();
    },
    clearDraft(key) {
      const entry = this.draftTakesByQuestion[key];
      if (entry?.take?.url) URL.revokeObjectURL(entry.take.url);
      delete this.draftTakesByQuestion[key];
      this.updateNavigationWarning();
    },
    clearTake() {
      this.clearDraft(this.currentDraftKey);
    },
    clearAllDrafts() {
      Object.keys(this.draftTakesByQuestion).forEach(this.clearDraft);
      this.draftSessionIdentity = createSecureUuid();
    },
    markCurrentQuestionForRerecord() {
      const questionOrder = this.currentQuestion?.order;
      if (!Number.isInteger(questionOrder) || questionOrder < 1) {
        return;
      }

      const uploadsByQuestion = new Map(
        this.recoveryUploads.map((upload) => [upload.questionOrder, upload]),
      );
      uploadsByQuestion.set(questionOrder, {
        questionOrder,
        uploadState: 'local_take_not_durable',
      });
      this.setRecoveryUploads([...uploadsByQuestion.values()]);
    },
    async cleanupCancelledUpload(
      sessionContext = this.captureSessionContext(),
    ) {
      if (!this.isSessionContextCurrent(sessionContext, { checkExpiry: false }) || !this.uploadDetails) {
        return false;
      }
      const { fileUuid, uploadId } = this.uploadDetails;
      if (this.isMultipartCompleted) {
        // A completed multipart upload has a registered source, so abort is no
        // longer valid; cancel the exact server-issued file instead.
        await Api.guidedRecording.cancelAcceptedTake({
          fileuuids: [fileUuid],
        }, sessionContext.sessionCapability);
      } else {
        try {
          await Api.guidedRecording.abortAcceptedTake({
            fileuuid: fileUuid,
            uploadid: uploadId,
          }, sessionContext.sessionCapability);
        } catch {
          // Keep the capability-scoped cancellation fallback for abort failures.
          await Api.guidedRecording.cancelAcceptedTake({
            fileuuids: [fileUuid],
          }, sessionContext.sessionCapability);
        }
      }
      if (!this.isSessionContextCurrent(sessionContext)) {
        return false;
      }
      // A successful cancellation is terminal for the session capability:
      // drop the capability, its recovery data and all local take state, and
      // let a fresh session be started from the start-session form.
      return this.resetToStartForm(sessionContext);
    },
    async resetToStartForm(sessionContext) {
      if (!this.isSessionContextCurrent(sessionContext)) {
        return false;
      }
      const invalidatedContext = this.invalidateSessionContext();
      this.releaseLocalResources(true);
      this.clearRecoveryState();
      this.recordingPageEntered = false;
      this.audioAction = '';
      this.isAudioActionInProgress = false;
      this.isSessionActionInProgress = false;
      this.sessionAction = '';
      this.consentAccepted = false;
      this.consentError = '';
      this.participantDisplayName = '';
      this.participantNameError = '';
      this.sessionCapability = '';
      this.sessionError = '';
      this.sessionProjection = null;
      this.sessionStatus = '';
      await this.$nextTick();
      if (this.sessionFreshness !== invalidatedContext.version
        || this.$route.params.code !== invalidatedContext.code) {
        return false;
      }
      this.$refs.participantName?.focus();
      return true;
    },
    async persistLocalTake(sessionContext) {
      if (!this.take || !this.currentQuestion || !this.isSessionContextCurrent(sessionContext)) {
        throw new Error('Invalid guided-recording local take context.');
      }

      if (!isValidUuid(this.takeIdempotencyKey)) {
        const idempotencyKey = createSecureUuid();
        if (!idempotencyKey) {
          throw new Error('Secure guided-recording idempotency key unavailable.');
        }
        this.takeIdempotencyKey = idempotencyKey;
      }
      const response = await Api.guidedRecording.acceptTakeLocally(
        sessionContext.code,
        sessionContext.sessionCapability,
        this.currentQuestion.order,
        this.takeIdempotencyKey,
      );
      const isRetryableLocalState = ['created', 'failed', 'cancelled'].includes(response?.state);
      if (
        !response
        || response.questionOrder !== this.currentQuestion.order
        || response.idempotencyKey !== this.takeIdempotencyKey
        || !isRetryableLocalState
      ) {
        throw new Error('Invalid guided-recording local take response.');
      }
      return response;
    },
    createUploadPayload() {
      const clientFileUuid = createSecureUuid();
      if (!clientFileUuid) {
        throw new Error('Secure guided-recording client file identifier unavailable.');
      }
      const clientFileId = `guided-take-${clientFileUuid}`;
      const filename = `guided-recording.${getTakeExtension(this.take.mimeType)}`;

      return {
        uploadername: this.participantDisplayName.trim(),
        roots: [
          {
            nodes: [],
            pods: [{ clientFileId, filename }],
          },
        ],
      };
    },
    async discardTake() {
      if (this.isSubmitting || this.uploadDetails) {
        return;
      }
      this.clearTake();
      this.submissionError = '';
      this.submissionProgress = 0;
      this.updateNavigationWarning();
      await this.$nextTick();
      await this.requestPreview();
    },
    async focusCurrentQuestion(sessionContext) {
      await this.$nextTick();
      if (!this.isSessionContextCurrent(sessionContext)) {
        return false;
      }
      if (this.recordingPageEntered) this.$refs.currentQuestionHeading?.focus();
      return true;
    },
    getMultipartDetails(response) {
      const pod = Array.isArray(response) ? response[0] : null;
      const multipartPost = pod?.multipartPost;
      const cleanupDetails = this.getMultipartCleanupDetails(response);
      const isValidPartCount = Number.isInteger(multipartPost?.partsCount) && multipartPost.partsCount > 0;
      const isValidChunkSize = Number.isInteger(multipartPost?.chunkSize) && multipartPost.chunkSize > 0;

      if (
        !cleanupDetails
        || !Array.isArray(multipartPost?.presignedUrls)
        || !isValidPartCount
        || !isValidChunkSize
        || multipartPost.presignedUrls.length !== multipartPost.partsCount
        || !multipartPost.presignedUrls.every(isValidPresignedUrl)
        || Math.ceil(this.take.blob.size / multipartPost.chunkSize) !== multipartPost.partsCount
      ) {
        throw new Error(INVALID_MULTIPART_RESPONSE_MESSAGE);
      }

      return {
        chunkSize: multipartPost.chunkSize,
        ...cleanupDetails,
        presignedUrls: multipartPost.presignedUrls,
      };
    },
    getMultipartCleanupDetails(response) {
      const pod = Array.isArray(response) ? response[0] : null;
      const multipartPost = pod?.multipartPost;
      if (
        !isSafeServerIdentifier(pod?.podid)
        || !isSafeServerIdentifier(pod?.fileuuid)
        || !isSafeServerIdentifier(multipartPost?.uploadId)
      ) {
        return null;
      }

      return {
        fileUuid: pod.fileuuid,
        podId: pod.podid,
        uploadId: multipartPost.uploadId,
      };
    },
    hasMultipartUploadInstructions() {
      return Number.isInteger(this.uploadDetails?.chunkSize)
        && this.uploadDetails.chunkSize > 0
        && Array.isArray(this.uploadDetails.presignedUrls);
    },
    async cleanupMalformedMultipartResponse(sessionContext) {
      this.isCancelling = true;
      try {
        return await this.cleanupCancelledUpload(sessionContext);
      } catch {
        // Keep the local take and validated server identifiers so the user
        // can retry the capability-scoped cancellation without exposing
        // malformed multipart metadata.
        if (this.isSessionContextCurrent(sessionContext)) {
          this.isCancelling = false;
          this.submissionError = this.$t('routes.guidedRecording.submissionFailed');
        }
        return false;
      }
    },
    async handleSessionRequestError(error, sessionContext) {
      if (!this.isSessionContextCurrent(sessionContext)) {
        return false;
      }
      if (isPublicUnavailableResponse(error)) {
        return this.markUnavailable(sessionContext);
      }

      this.sessionError = this.$t('routes.guidedRecording.sessionReadError');
      return true;
    },
    applyBrandColors(brand) {
      for (const themeName of ['dark', 'light']) {
        const colors = this.$vuetify.theme.themes[themeName].colors;
        const defaults = DEFAULT_THEME_COLORS[themeName];
        colors.primary = brand?.color || defaults.primary;
        colors['on-list'] = brand?.color || defaults['on-list'];
        colors.background = brand?.backgroundColor || defaults.background;
        colors['tw-surface'] = brand?.foregroundColor || defaults['tw-surface'];
        colors.surface = brand?.foregroundColor || defaults.surface;
        colors['tw-surface-accent'] = brand?.foregroundAccentColor || defaults['tw-surface-accent'];
        colors.list = brand?.foregroundAccentColor || defaults.list;
      }
    },
    async loadInvitation() {
      const recoveryCompletion = getAudioGateCompletion(this.$route.params.code);
      this.clearInvitationExpiryTimer();
      this.invalidateSessionContext();
      this.releaseLocalResources(true);
      // Reset page resources without erasing a recovered upload's finalization credentials.
      if (recoveryCompletion) {
        saveAudioGateCompletion(this.$route.params.code, recoveryCompletion);
      }
      this.autocueAlignment = 'center';
      this.autocueFontSize = 24;
      this.autocueScrollSpeed = 40;
      this.consentAccepted = false;
      this.consentError = '';
      this.recordingPageEntered = false;
      this.invitation = null;
      this.isLoading = true;
      this.isUnavailable = false;
      this.invitationLoadFailed = false;
      this.participantDisplayName = '';
      this.participantNameError = '';
      this.isSessionActionInProgress = false;
      this.isStartingSession = false;
      this.recoveryAttestation = null;
      this.recoveryData = null;
      this.recoveryState = null;
      this.recoveryUploads = [];
      this.sessionAction = '';
      this.sessionCapability = '';
      const sessionContext = this.captureSessionContext();
      this.recoveryData = getRecoveryData(sessionContext.code);
      this.sessionError = '';
      this.sessionProjection = null;
      this.sessionStatus = '';
      try {
        const invitation = await Api.guidedRecording.getPublicInvitation(sessionContext.code);
        if (!invitation || typeof invitation.title !== 'string') {
          throw new Error('Invalid guided-recording invitation response.');
        }
        if (!this.isSessionContextCurrent(sessionContext)) {
          return false;
        }
        this.invitation = invitation;
        this.applyBrandColors(invitation.brand);
        if (!this.scheduleInvitationExpiry()) {
          return false;
        }
        this.isLoading = false;
        const recoveryData = getRecoveryData(sessionContext.code);
        if (!recoveryData) {
          return true;
        }
        this.recoveryData = recoveryData;
        return this.resumeSession(recoveryData, sessionContext);
      } catch (error) {
        if (isPublicUnavailableResponse(error)) {
          return this.markUnavailable(sessionContext);
        }
        if (!this.isSessionContextCurrent(sessionContext)) {
          return false;
        }
        this.invitationLoadFailed = true;
        this.recoveryAttestation = null;
        this.recoveryUploads = [];
        this.recoveryState = 'failed';
        this.sessionError = this.$t('routes.guidedRecording.sessionReadError');
        return false;
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.isLoading = false;
        }
      }
    },
    async markUnavailable(sessionContext = this.captureSessionContext()) {
      if (!this.isSessionContextCurrent(sessionContext, { checkExpiry: false })) {
        return false;
      }
      const unavailableContext = this.invalidateSessionContext();
      this.clearInvitationExpiryTimer();
      this.stopAudioStatusPolling();
      this.releaseLocalResources(true);
      this.recordingPageEntered = false;
      this.consentAccepted = false;
      this.consentError = '';
      this.invitation = null;
      this.isUnavailable = true;
      this.isLoading = false;
      this.invitationLoadFailed = false;
      this.participantDisplayName = '';
      this.participantNameError = '';
      this.isSessionActionInProgress = false;
      this.isStartingSession = false;
      this.sessionAction = '';
      this.sessionCapability = '';
      this.sessionError = '';
      this.sessionProjection = null;
      this.sessionStatus = '';
      this.clearRecoveryState();
      await this.$nextTick();
      if (this.sessionFreshness !== unavailableContext.version
        || this.$route.params.code !== unavailableContext.code) {
        return false;
      }
      this.$refs.unavailableHeading?.focus();
      return true;
    },
    nextQuestion() {
      return this.runSessionAction('nextPublicSessionQuestion', 'next');
    },
    previousQuestion() {
      return this.runSessionAction('previousPublicSessionQuestion', 'previous');
    },
    selectQuestion(order) {
      if (!Number.isInteger(order) || order === this.currentQuestion?.order) return;
      return this.runSessionAction('selectPublicSessionQuestion', 'select', order);
    },
    questionRequirementLabel(question) {
      return this.$t(`routes.guidedRecording.question${question.required ? 'Required' : 'Optional'}`);
    },
    questionStateColor(state) {
      if (state === 'completed') {
        return 'success';
      }
      if (state === 'skipped') {
        return 'secondary';
      }
      return 'primary';
    },
    questionStateIcon(state) {
      if (state === 'completed') {
        return 'mdi-check-circle-outline';
      }
      if (state === 'skipped') {
        return 'mdi-skip-next-circle-outline';
      }
      return 'mdi-circle-outline';
    },
    questionStateLabel(state) {
      return this.$t(`routes.guidedRecording.questionState${state[0].toUpperCase()}${state.slice(1)}`);
    },
    questionUploadStateLabel(question) {
      const upload = this.recoveryUploads.find(({ questionOrder }) => questionOrder === question.order);
      if (!upload) {
        return '';
      }
      if (upload.uploadState === 'confirmed') {
        return this.$t('routes.guidedRecording.recovery.progressConfirmed');
      }
      if (upload.uploadState === 'upload_in_progress') {
        return this.$t('routes.guidedRecording.recovery.uploadInProgress');
      }
      if (upload.uploadState === 'failed') {
        return this.$t('routes.guidedRecording.recovery.progressFailed');
      }
      if (upload.uploadState === 'local_take_not_durable') {
        return this.$t('routes.guidedRecording.recovery.takeNotDurable');
      }
      if (upload.audio?.status) {
        if (upload.audio.status === GUIDED_RECORDING_AUDIO_PROJECTION_FAILURE_STATUS) {
          return this.$t('routes.guidedRecording.audioResult.statusProjectionFailure');
        }
        const statusKey = `${upload.audio.status[0].toUpperCase()}${upload.audio.status.slice(1)}`;
        return this.$t(`routes.guidedRecording.audioResult.status${statusKey}`);
      }
      return this.$t('routes.guidedRecording.recovery.progressNotStarted');
    },
    currentQuestionUploadState() {
      const upload = this.recoveryUploads.find(
        ({ questionOrder }) => questionOrder === this.currentQuestion?.order,
      );
      return upload?.uploadState || '';
    },
    syncAudioStatusPolling() {
      const shouldPoll = this.isSessionActive
        && (this.currentQuestionAudioResult?.status === 'pending' || this.isTakeProcessing);
      if (!shouldPoll || typeof window === 'undefined') {
        this.stopAudioStatusPolling();
        return;
      }
      if (this.audioStatusPollTimer !== null) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.audioStatusPollTimer = window.setInterval(async () => {
        const refreshed = await this.refreshUploadStatus(sessionContext);
        if (refreshed && this.isSessionContextCurrent(sessionContext) && this.isTakeProcessing) {
          await this.completeArrivedAudioResult();
        }
      }, GUIDED_RECORDING_AUDIO_STATUS_POLL_INTERVAL_MS);
    },
    stopAudioStatusPolling() {
      if (this.audioStatusPollTimer !== null && typeof window !== 'undefined') {
        window.clearInterval(this.audioStatusPollTimer);
      }
      this.audioStatusPollTimer = null;
    },
    questionUploadStateIcon(question) {
      const upload = this.recoveryUploads.find(({ questionOrder }) => questionOrder === question.order);
      if (!upload) {
        return '';
      }
      if (upload.uploadState === 'confirmed') {
        return 'mdi-check-circle';
      }
      if (upload.uploadState === 'upload_in_progress') {
        return 'mdi-progress-upload';
      }
      if (upload.uploadState === 'failed') {
        return 'mdi-alert-circle';
      }
      if (upload.uploadState === 'local_take_not_durable') {
        return 'mdi-alert-outline';
      }
      if (upload.uploadState === 'audio_pending') {
        return 'mdi-progress-clock';
      }
      if (upload.uploadState === 'audio_warning') {
        return 'mdi-alert-outline';
      }
      if (upload.uploadState === 'audio_failed') {
        return 'mdi-alert-circle-outline';
      }
      if (upload.uploadState === 'audio_unavailable') {
        return 'mdi-help-circle-outline';
      }
      return '';
    },
    questionUploadStateColor(question) {
      const upload = this.recoveryUploads.find(({ questionOrder }) => questionOrder === question.order);
      if (!upload) {
        return '';
      }
      if (upload.uploadState === 'confirmed') {
        return 'success';
      }
      if (upload.uploadState === 'upload_in_progress') {
        return 'primary';
      }
      if (upload.uploadState === 'failed') {
        return 'error';
      }
      if (upload.uploadState === 'local_take_not_durable') {
        return 'warning';
      }
      if (upload.uploadState === 'audio_pending') {
        return 'primary';
      }
      if (upload.uploadState === 'audio_warning' || upload.uploadState === 'audio_unavailable') {
        return 'warning';
      }
      if (upload.uploadState === 'audio_failed') {
        return 'error';
      }
      return '';
    },
    releaseLocalResources(abortUpload = false) {
      if (abortUpload) {
        this.uploadAbortController?.abort();
      }
      this.clearQuestionResources();
      this.clearAllDrafts();
      this.disposeDevices();
      this.isCancelling = false;
      this.isSubmitting = false;
      this.uploadAbortController = null;
    },
    captureSessionContext() {
      return {
        code: this.$route.params.code,
        sessionCapability: this.sessionCapability,
        version: this.sessionFreshness,
      };
    },
    invalidateSessionContext() {
      this.sessionFreshness += 1;
      return this.captureSessionContext();
    },
    isSessionContextCurrent(sessionContext, { checkExpiry = true } = {}) {
      const isCurrent = sessionContext?.version === this.sessionFreshness
        && sessionContext.code === this.$route.params.code
        && sessionContext.sessionCapability === this.sessionCapability;
      if (isCurrent && checkExpiry && this.invitation && !this.hasFutureInvitationExpiry()) {
        this.markUnavailable(sessionContext);
        return false;
      }
      return isCurrent;
    },
    setSessionCapability(sessionCapability) {
      this.sessionCapability = sessionCapability;
      return this.invalidateSessionContext();
    },
    async refreshSession({ focusCurrent = false } = {}) {
      if (!this.sessionCapability) {
        return false;
      }

      const sessionContext = this.captureSessionContext();
      try {
        const session = await Api.guidedRecording.getPublicSession(
          sessionContext.code,
          sessionContext.sessionCapability,
        );
        if (!this.isSessionContextCurrent(sessionContext)) {
          return false;
        }
        const applied = await this.applySessionProjection(session, sessionContext, { focusCurrent });
        if (applied) {
          await this.refreshUploadStatus(sessionContext);
        }
        return applied;
      } catch (error) {
        if (!this.isSessionContextCurrent(sessionContext)) {
          return false;
        }
        if (isInvalidSessionProjectionError(error)) {
          this.releaseLocalResources();
          this.sessionProjection = null;
          this.sessionStatus = '';
        }
        await this.handleSessionRequestError(error, sessionContext);
        return false;
      }
    },
    async resumeSession(recoveryData, sessionContext) {
      this.recoveryState = 'checking';
      let currentContext = sessionContext;
      try {
        const response = await Api.guidedRecording.resumePublicSession(
          sessionContext.code,
          recoveryData.recoveryCredential,
          recoveryData.browserContextId,
        );
        if (!this.isSessionContextCurrent(currentContext)) {
          return false;
        }
        const attestation = toSafeAttestation(response?.attestation);
        if (!attestation) {
          throw new Error('Invalid guided-recording resume response.');
        }
        this.recoveryState = 'resuming';
        this.recoveryAttestation = attestation;
        const recoveryUploads = toSafeUploadReferences(response?.uploads);
        if (!recoveryUploads) {
          throw new Error('Invalid guided-recording upload recovery response.');
        }
        this.recoveryUploads = recoveryUploads;
        this.participantDisplayName = attestation.participantDisplayName;
        this.consentAccepted = this.recordingPageEntered ? attestation.consentAccepted : false;
        const freshSessionCapability = typeof response?.sessionCapability === 'string'
          ? response.sessionCapability
          : '';
        if (freshSessionCapability) {
          this.setSessionCapability(freshSessionCapability);
          const audioGateCompletion = getAudioGateCompletion(sessionContext.code);
          saveRecoveryData(sessionContext.code, {
            ...recoveryData,
            sessionCapability: freshSessionCapability,
            ...(audioGateCompletion ? { audioGateCompletion } : {}),
          });
        } else if (recoveryData.sessionCapability) {
          this.setSessionCapability(recoveryData.sessionCapability);
        } else {
          throw new Error('Missing guided recording session capability.');
        }
        currentContext = this.captureSessionContext();
        await this.applySessionProjection(response.session, currentContext, {
          focusCurrent: this.recordingPageEntered,
        });
        if (!this.isSessionContextCurrent(currentContext)) {
          return false;
        }
        this.setRecoveryUploads(recoveryUploads);
        if (!this.isSubmitted) {
          this.recoveryState = 'resumed';
        }
        await this.refreshUploadStatus(currentContext);
        return true;
      } catch (error) {
        if (!this.isSessionContextCurrent(currentContext)) {
          return false;
        }
        if (isPublicUnavailableResponse(error)) {
          return this.markUnavailable(currentContext);
        }
        this.recoveryAttestation = null;
        this.recoveryUploads = [];
        this.recoveryState = 'failed';
        this.releaseSource();
        this.sessionCapability = '';
        this.sessionError = this.$t('routes.guidedRecording.sessionReadError');
        return true;
      }
    },
    async refreshUploadStatus(sessionContext = this.captureSessionContext()) {
      if (!this.sessionCapability || !this.isSessionContextCurrent(sessionContext)) {
        return false;
      }
      try {
        const uploadStatus = await Api.guidedRecording.getUploadStatus(
          sessionContext.code,
          sessionContext.sessionCapability,
        );
        if (!this.isSessionContextCurrent(sessionContext)) {
          return false;
        }
        const recoveryUploads = toSafeUploadReferences(uploadStatus);
        if (!recoveryUploads) {
          return false;
        }
        this.setRecoveryUploads(recoveryUploads);
        return true;
      } catch (error) {
        return this.handleSessionRequestError(error, sessionContext);
      }
    },
    updateNavigationWarning() {
      if (typeof window === 'undefined') {
        return;
      }
      if (this.recorderBusy || this.recorderIsRecording || this.isSubmitting
        || Object.values(this.draftTakesByQuestion).some(({ take }) => Boolean(take))) {
        window.onbeforeunload = () => this.$t('routes.guidedRecording.recovery.leaveWarning');
      } else {
        window.onbeforeunload = null;
      }
    },
    async runSessionAction(method, action, order) {
      if (!this.scheduleInvitationExpiry()) {
        return;
      }
      if (!this.isSessionActive || this.isSessionActionDisabled) {
        return;
      }

      const sessionContext = this.captureSessionContext();
      this.isSessionActionInProgress = true;
      this.sessionAction = action;
      this.sessionError = '';
      try {
        await Api.guidedRecording[method](sessionContext.code, sessionContext.sessionCapability, order);
        if (!this.isSessionContextCurrent(sessionContext)) {
          return;
        }
        await this.refreshSession({ focusCurrent: true });
      } catch (error) {
        await this.handleSessionRequestError(error, sessionContext);
      } finally {
        if (this.isSessionContextCurrent(sessionContext)) {
          this.isSessionActionInProgress = false;
          this.sessionAction = '';
        }
      }
    },
    setTake(recordedTake) {
      if (!this.isSessionContextCurrent(this.captureSessionContext())) {
        return;
      }
      if (!this.isSessionActive || !recordedTake || typeof recordedTake !== 'object') {
        return;
      }
      const replacementIdempotencyKey = this.takeIdempotencyKey;
      this.clearTake();
      this.takeIdempotencyKey = replacementIdempotencyKey;
      const hasBlob = typeof Blob !== 'undefined'
        && recordedTake.blob instanceof Blob
        && recordedTake.blob.size > 0;
      this.take = {
        ...recordedTake,
        url: hasBlob ? URL.createObjectURL(recordedTake.blob) : '',
      };
      this.submissionError = '';
      this.submissionProgress = 0;
      this.uploadDetails = null;
      this.isMultipartCompleted = false;
      this.isRetryableSubmissionFailure = false;
      this.updateNavigationWarning();
    },
    setRecoveryUploads(uploads) {
      const completedQuestionOrders = new Set(
        this.sessionQuestions
          .filter(({ state }) => state === 'completed')
          .map(({ order }) => order),
      );
      const uploadsByQuestion = new Map(
        uploads.map((upload) => [upload.questionOrder, upload]),
      );

      completedQuestionOrders.forEach((questionOrder) => {
        uploadsByQuestion.set(questionOrder, {
          questionOrder,
          uploadState: 'confirmed',
        });
      });

      this.recoveryUploads = [...uploadsByQuestion.values()]
        .sort((first, second) => first.questionOrder - second.questionOrder);

      if (this.currentQuestionAudioResult) {
        this.clearTake();
        this.isRetryableSubmissionFailure = false;
        this.submissionError = '';
        this.submissionProgress = 0;
      }
      if (this.isTakeProcessing) {
        this.submissionError = '';
        this.isRetryableSubmissionFailure = false;
      }
      this.syncAudioStatusPolling();
    },
    skipQuestion() {
      return this.runSessionAction('skipPublicSessionQuestion', 'skip');
    },
    async uploadMultipartTake(sessionContext, uploadAbortController) {
      const { blob } = this.take;
      const { chunkSize, presignedUrls } = this.uploadDetails;
      const uploadedParts = [];

      for (let index = 0; index < presignedUrls.length; index += 1) {
        if (!this.isSessionContextCurrent(sessionContext)) {
          return null;
        }
        const partNumber = index + 1;
        const start = index * chunkSize;
        const chunk = blob.slice(start, Math.min(start + chunkSize, blob.size));
        const response = await axios.put(presignedUrls[index], chunk, {
          onUploadProgress: ({ loaded }) => {
            if (this.isSessionContextCurrent(sessionContext)) {
              this.submissionProgress = Math.min(99, Math.round(((start + loaded) / blob.size) * 100));
            }
          },
          signal: uploadAbortController.signal,
        });
        if (!this.isSessionContextCurrent(sessionContext)) {
          return null;
        }
        const etag = response.headers.etag || response.headers.ETag;
        if (typeof etag !== 'string' || !etag) {
          throw new Error('Multipart part response did not include an ETag.');
        }
        uploadedParts.push({ ETag: etag, PartNumber: partNumber });
      }

      return uploadedParts.sort((first, second) => first.PartNumber - second.PartNumber);
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-recording-public__top-bar {
    align-items: center;
    display: flex;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 100;
  }

  .guided-recording-public__main {
    padding-top: 76px;
  }

  .guided-recording-public {
    margin-inline: auto;
    max-width: 1440px;
    min-height: 100%;
    width: 100%;
  }

  .guided-recording-public__column {
    padding-inline: 0;
  }

  .guided-recording-public__heading {
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .guided-recording-public__heading--initial {
    font-size: clamp(1.75rem, 4vw, 2.25rem);
  }

  .guided-recording-public__copy,
  .guided-recording-public__question-text {
    white-space: pre-wrap;
  }

  .guided-recording-public__content {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    margin: 0;
    gap: 12px;
  }

  .guided-recording-public__sidebar,
  .guided-recording-public__workspace {
    width: auto;
    max-width: none;
    min-width: 0;
    padding: 0;
  }

  .guided-recording-public__content--recording .guided-recording-public__sidebar { order: 2; }
  .guided-recording-public__content--recording .guided-recording-public__workspace { order: 1; }

  .guided-recording-public__instructions {
    max-height: 24vh;
    overflow: auto;
    overflow-wrap: anywhere;
  }

  .guided-recording-public__instructions ul { padding-inline-start: 20px; }

  .guided-recording-public__question-header { flex-wrap: wrap; }


  .guided-recording-public__question-button {
    align-items: center;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 8px;
    min-height: 44px;
    padding: 8px;
    text-align: start;
    width: 100%;
    border-radius: inherit;
  }

  .guided-recording-public__question-button:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  .guided-recording-public__question-button:disabled { opacity: 0.65; }

  @media (min-width: 960px) {
    .guided-recording-public__content--recording {
      grid-template-columns: clamp(260px, 30%, 360px) minmax(0, 1fr);
      align-items: start;
    }
    .guided-recording-public__content--recording .guided-recording-public__sidebar {
      order: 0;
      max-height: calc(100dvh - 132px);
      overflow-y: auto;
      overscroll-behavior: contain;
    }
    .guided-recording-public__progress-list { max-height: 22vh; overflow: auto; }
  }

  @media (min-width: 1280px) {
    .guided-recording-public__content:not(.guided-recording-public__content--recording) {
      grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
      align-items: start;
    }
  }

  .guided-recording-public__consent-statement {
    border-inline-start: 3px solid rgb(var(--v-theme-primary));
    padding-inline-start: 16px;

    :deep(.md-editor-preview-wrapper),
    :deep(.md-editor-preview) {
      background: transparent;
      padding: 0;
    }

    :deep(.md-editor-preview) {
      line-height: 1.6;

      h1 {
        font-size: 1.25rem;
        margin-bottom: 0.25rem;
      }

      h2 {
        font-size: 1rem;
        margin-top: 1.5rem;
        margin-bottom: 0.25rem;
      }

      p {
        margin-bottom: 0.75rem;
      }
    }
  }

  .guided-recording-public__progress-list {
    display: grid;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .guided-recording-public__progress-item {
    border: 1px solid transparent;
    border-radius: 8px;
    min-width: 0;
  }

  .guided-recording-public__progress-item--current {
    background: rgb(var(--v-theme-tw-surface-accent));
    border-color: rgb(var(--v-theme-primary));
  }

  .guided-recording-public__progress-item--completed,
  .guided-recording-public__progress-item--skipped {
    background: rgb(var(--v-theme-tw-surface-accent));
  }

  .guided-recording-public__current-question {
    background: rgb(var(--v-theme-tw-surface-accent));
    border-radius: 10px;
    padding: 8px;
  }

  .guided-recording-public__current-heading:focus,
  .guided-recording-public__unavailable-heading:focus {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }

  .guided-recording-public__question-kind {
    border: 1px solid currentcolor;
    border-radius: 999px;
    font-weight: 700;
    padding: 4px 8px;
    white-space: nowrap;
  }

  .guided-recording-public__question-detail {
    border-left: 3px solid rgb(var(--v-theme-primary));
    padding-left: 12px;
  }

  .guided-recording-public__question-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .guided-recording-public__question-actions > .v-btn {
    min-height: 44px;
  }

  @media (min-width: 960px) {
    .guided-recording-public__current-question { padding: 8px; }
  }

  @media (max-width: 600px) {

    .guided-recording-public__question-kind {
      white-space: normal;
    }

    .guided-recording-public__main {
      padding-top: 56px;
    }
  }
}
</style>
